import { useCallback, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import { ChevronIcon } from 'src/components/ChevronIcon';
import { CustomMenu } from 'src/components/CustomMenu';
import { ToneOfVoiceList } from 'src/components/ToneOfVoiceList';
import { GENERATION_IMPROVE_MODE } from 'src/data/generation';
import { ROTATE_DIRECTION } from 'src/data/ui';

import { InternalListItem } from '../InternalListItem';

import { useStyles } from './useStyles';

const SUBMENU_PROPS = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  autoFocus: false,
  disableAutoFocusItem: true,
  disableAutoFocus: true,
  disableEnforceFocus: true,
  disableRestoreFocus: true,
  disableScrollLock: true,
};

const HOVER_MENU_TIMEOUT = 100;

const propTypes = {
  className: PropTypes.string,
  ToneOfVoiceListProps: PropTypes.shape(ToneOfVoiceList.propTypes).isRequired,
  disabled: PropTypes.bool,
};

const defaultProps = {
  className: '',
  disabled: false,
};

export const ChangeTovListItem = ({ className, ToneOfVoiceListProps, disabled }) => {
  const styles = useStyles();

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const menuItemRef = useRef(null);

  const debouncedOpen = useDebouncedCallback(() => {
    setIsSubmenuOpen(true);
  }, HOVER_MENU_TIMEOUT);

  const debouncedClose = useDebouncedCallback(() => {
    setIsSubmenuOpen(false);
  }, HOVER_MENU_TIMEOUT);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    debouncedClose.cancel();
    setIsSubmenuOpen(true);
  }, [disabled, debouncedClose]);

  const handleMouseEnter = useCallback(() => {
    if (disabled) {
      return;
    }

    debouncedClose.cancel();
    debouncedOpen();
  }, [disabled, debouncedOpen, debouncedClose]);

  const handleMouseLeave = useCallback(() => {
    debouncedOpen.cancel();
    debouncedClose();
  }, [debouncedOpen, debouncedClose]);

  const handleSubmenuClose = useCallback(() => {
    debouncedOpen.cancel();
    setIsSubmenuOpen(false);
  }, [debouncedOpen]);

  const originalOnSelect = ToneOfVoiceListProps.onSelect;
  const handleTovListSelect = useCallback(
    (result) => {
      debouncedOpen.cancel();
      setIsSubmenuOpen(false);

      originalOnSelect(result);
    },
    [debouncedOpen, originalOnSelect]
  );

  return (
    <div
      className={cx(styles.wrapper, {
        [styles.open]: isSubmenuOpen,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <InternalListItem
        className={cx(styles.root, className)}
        ref={menuItemRef}
        mode={GENERATION_IMPROVE_MODE.changeTov}
        onClick={handleClick}
        disabled={disabled}
        endIcon={
          <ChevronIcon
            className={styles.iconSubmenu}
            direction={ROTATE_DIRECTION.right}
          />
        }
      />

      <CustomMenu
        className={styles.submenu}
        classes={{ paper: styles.submenu_paper }}
        anchorEl={menuItemRef?.current}
        open={isSubmenuOpen}
        onClose={handleSubmenuClose}
        disabled={disabled}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        disablePortal
        {...SUBMENU_PROPS}
      >
        <ToneOfVoiceList
          className={styles.toneOfVoiceList}
          {...ToneOfVoiceListProps}
          onSelect={handleTovListSelect}
        />
      </CustomMenu>
    </div>
  );
};

ChangeTovListItem.displayName = 'ChangeTovListItem';
ChangeTovListItem.propTypes = propTypes;
ChangeTovListItem.defaultProps = defaultProps;
